<template>
  <v-container v-if="$can('browse', 'Staff')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Staff
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          color="primary"
          class="ma-0"
          @click="openStaffEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.name"
          solo
          hide-details
          clearable
          label="Name"
          dense
          class="mb-2"
        />
        <v-autocomplete
          v-model="filtersDirty.role"
          :items="roles.data"
          :loading="roles.loading"
          solo
          hide-details
          clearable
          item-value="id"
          item-text="title"
          dense
          label="Role"
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="staff.meta.from"
          :to="staff.meta.to"
          :total="staff.meta.total"
          :last-page="staff.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="staff.data"
          :loading="staff.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="staff.meta.total"
          hide-default-footer
          must-sort
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <staff-row
              :staff="item"
              @click.native="openStaffEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no staff members at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  class="ma-0"
                  small
                  @click="openStaffEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="staff.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <staff-edit-dialog
      :visible.sync="staffEditDialog"
      :staff="staffDirty"
      @staff:created="staffCreated"
      @staff:updated="staffUpdated"
      @staff:deleted="staffDeleted"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import Staff from '@/models/Staff'
  import StaffRow from '@/components/business/staff/StaffRow'
  import StaffEditDialog from '@/components/business/staff/StaffEditDialog'
  import TableFooter from '@/components/helpers/TableFooter'
  import Role from '@/models/Role'

  export default {
    name: 'StaffIndex',
    components: {
      StaffRow,
      StaffEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        staff: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Email', value: 'email', sortable: false },
          { text: 'Telephone', value: 'tel', sortable: false },
          { text: 'Roles', value: 'user.roles', sortable: false }
        ],
        staffDirty: undefined,
        staffEditDialog: false,
        filtersClean: Staff.emptyFilters,
        filtersDirty: cloneDeep(Staff.emptyFilters),
        roles: {
          data: [],
          loading: false
        }
      }
    },
    created() {
      this.fetchRoles()
      this.fetchStaff()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchStaff()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchStaff() {
        this.staff.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await Staff.api.fetch(queryParams)

          this.staff.data = result.data
          this.staff.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.staff.loading = false
        }
      },
      fetchRoles: async function() {
        this.roles.loading = true

        try {
          this.roles.data = await Role.api.fetch()
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.roles.loading = false
        }
      },
      openStaffEditDialog(staff = undefined) {
        this.staffDirty = cloneDeep(staff)
        this.staffEditDialog = true
      },
      clearFilters() {
        this.filtersDirty = cloneDeep(Staff.emptyFilters)
      },
      staffCreated(staff) {
        this.staff.data.unshift(staff)
      },
      staffUpdated(staff) {
        const index = this.staff.data.findIndex(s => s.id === staff.id)

        this.staff.data.splice(index, 1, staff)
      },
      staffDeleted(staffId) {
        const index = this.staff.data.findIndex(s => s.id === staffId)

        this.staff.data.splice(index, 1)
      }
    }
  }
</script>
